// File: frontend/src/utils/searchUtils.ts
// src/utils/searchUtils.ts
import { SearchEntryResult } from "../models/SearchModel";
import { generateMatchUrlFromEntry } from "./slugGenerator";

/**
 * Folds a string down to plain lowercase ASCII for client-side "contains"
 * matching in pickers and typeaheads.
 *
 * Why this exists: JS `toLowerCase()` turns the Turkish dotted capital İ
 * (U+0130) into "i" + a COMBINING DOT ABOVE (U+0307), not a plain "i". So
 * "İspanya".toLowerCase() is "i\u0307spanya" and `.includes("ispanya")` is
 * false — typing the club name with a lowercase i finds nothing, while typing
 * it with a capital İ works. Turkish keyboards produce İ naturally; a German
 * (or US) layout does not, so the picker only worked for one half of the
 * keyboards in use.
 *
 * The same class of mismatch affects every accented character: someone typing
 * "besiktas" should still find "Beşiktaş", and "munchen" should find "München".
 * Normalizing BOTH the query and the candidate through this function makes the
 * comparison diacritic-blind in one place.
 *
 * Steps:
 *   1. Replace Turkish letters that have no usable decomposition (İ, I, ı)
 *      BEFORE lowercasing — see above for why İ cannot wait.
 *   2. Lowercase.
 *   3. NFD-decompose and strip every remaining combining mark, which handles
 *      ş ğ ü ö ç as well as every other Latin diacritic (š, é, ā, ...).
 *
 * This deliberately does NOT hyphenate or strip punctuation — it is a search
 * comparison helper, not a slug generator (see slugGenerator.ts for that).
 */
export function normalizeSearchText(value: string): string {
  if (!value) return "";
  return value
    // İ (U+0130) → i and dotless ı (U+0131) → i must happen before lowercasing:
    // İ would otherwise decompose into i + U+0307, and ı has no decomposition
    // at all so step 3 would leave it untouched and "Kasımpaşa" would never
    // match a typed "kasimpasa".
    .replace(/İ/g, "i")
    .replace(/I/g, "i")
    .replace(/ı/g, "i")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Sport type enum name → URL slug segment.
 * Mirrors the same map used in SearchBar.tsx and SearchResultsPage.tsx
 * for team URL construction.
 */
const SPORT_SLUG: Record<string, string> = {
  FOOTBALL:          "futbol",
  BASKETBALL:        "basketbol",
  VOLLEYBALL:        "voleybol",
  AMERICAN_FOOTBALL: "amerikan-futbol",
  BASEBALL:          "beyzbol",
  ICE_HOCKEY:        "buz-hokeyi",
  HANDBALL:          "hentbol",
};

/**
 * Builds the page URL for a search entry result based on its topicType.
 *
 * Returns the base page URL (without the #entry-{id} fragment) so the
 * caller can append it as needed.
 *
 * Returns null for unknown/unsupported topic types.
 */
export function buildEntryUrl(en: SearchEntryResult): string | null {
  switch (en.topicType) {
    case "MATCH": {
      if (en.matchId && en.homeTeam && en.awayTeam && en.matchDate) {
        return generateMatchUrlFromEntry(
          en.homeTeam,
          en.awayTeam,
          new Date(en.matchDate + "T00:00:00"),
          en.matchId
        );
      }
      return null;
    }

    case "PERSON": {
      if (en.personSlug) return `/kisi/${en.personSlug}`;
      return null;
    }

    case "CLUB": {
      if (en.clubSlug) return `/kulup/${en.clubSlug}`;
      return null;
    }

    case "TEAM": {
      if (en.teamClubSlug && en.teamSportType) {
        const sportPart    = SPORT_SLUG[en.teamSportType] ?? en.teamSportType.toLowerCase();
        const genderSuffix = en.teamGender === "FEMALE" ? "-kadin" : en.teamGender === "MIXED" ? "-karma" : "";
        const codeSuffix   = en.teamCode ? `-${en.teamCode}` : "";
        return `/takim/${en.teamClubSlug}/${sportPart}${genderSuffix}${codeSuffix}`;
      }
      return null;
    }

    case "CUSTOM": {
      if (en.customTopicSlug) return `/gundem/${en.customTopicSlug}`;
      return null;
    }

    default:
      return null;
  }
}

/**
 * What the entry is "about" — a human-readable context label shown alongside
 * the author name in search results. Every topic type has its own display-name
 * field(s) on SearchEntryResult; returns null if none apply (shouldn't happen
 * in a consistent DB, but keeps callers safe).
 */
export function entryTopicLabel(en: SearchEntryResult): string | null {
  switch (en.topicType) {
    case "MATCH":
      return en.homeTeam ? `${en.homeTeamTr || en.homeTeam} – ${en.awayTeamTr || en.awayTeam}` : null;
    case "PERSON":
      return en.personFullName;
    case "CLUB":
      return en.clubNameTr || en.clubName;
    case "TEAM":
      return en.teamName;
    case "CUSTOM":
      return en.customTopicTitle;
    default:
      return null;
  }
}